import React from "react";
import { useTranslation } from "react-i18next";
import AppTable from "../../components/ui/AppTable";
import ListToolbar from "../../components/ui/ListToolbar";
import AppButton from "../../components/ui/AppButton";
import { formatNumericDate } from "../../utils/formatters";

const UMTransferList = ({ transfers, loading, totalElements, clubs, filters, onFilterChange, onClearFilters }) => {
  const { t, i18n } = useTranslation("universityManager");
  const { t: tCommon } = useTranslation("common");

  const TRANSFER_STATUS_LABEL_KEYS = { COMPLETED: "completed", PENDING: "pending", FAILED: "failed", REFUNDED: "refunded" };
  const getStatusBadge = (status) => {
    const badges = { COMPLETED: { color: "success", icon: "fa-check-circle" }, PENDING: { color: "warning", icon: "fa-clock" }, FAILED: { color: "danger", icon: "fa-times-circle" }, REFUNDED: { color: "secondary", icon: "fa-undo" } };
    const badge = badges[status] || { color: "secondary", icon: "fa-question-circle" };
    const label = TRANSFER_STATUS_LABEL_KEYS[status] ? t(`finance.transfers.list.${TRANSFER_STATUS_LABEL_KEYS[status]}`) : status;
    return (
      <span className={`badge bg-${badge.color} bg-opacity-10 text-${badge.color} fw-bold`}>
        <i className={`fas ${badge.icon} me-1`}></i>{label}
      </span>
    );
  };

  const hasActiveFilters = Object.values(filters || {}).some((v) => v);

  return (
    <div className="transfer-list">
      <ListToolbar
        title={t("finance.transfers.list.title")}
        subtitle={t("finance.transfers.list.subtitle", { count: totalElements })}
        filterBadges={hasActiveFilters ? (
          <>
            {filters.clubId && <span className="badge bg-primary">Club: {clubs.find((c) => c.id.toString() === filters.clubId)?.nameEn}</span>}
            {filters.type && <span className="badge bg-info">Type: {filters.type === "APPLYING_EVENT" ? t("finance.transfers.list.eventType") : t("finance.transfers.list.clubType")}</span>}
            {filters.status && <span className="badge bg-warning">Status: {filters.status}</span>}
            {filters.transferType && <span className="badge bg-secondary">Transfer Type: {filters.transferType}</span>}
            {filters.dateFrom && <span className="badge bg-info">From: {formatNumericDate(filters.dateFrom, i18n.language)}</span>}
            {filters.dateTo && <span className="badge bg-info">To: {formatNumericDate(filters.dateTo, i18n.language)}</span>}
          </>
        ) : null}
      >
        <div className="row g-3">
          <div className="col-md-3">
            <label className="form-label fw-medium">{t("finance.transfers.list.dateFrom")}</label>
            <input type="date" className="form-control" value={filters.dateFrom || ""} onChange={(e) => onFilterChange("dateFrom", e.target.value)} />
          </div>
          <div className="col-md-3">
            <label className="form-label fw-medium">{t("finance.transfers.list.dateTo")}</label>
            <input type="date" className="form-control" value={filters.dateTo || ""} onChange={(e) => onFilterChange("dateTo", e.target.value)} />
          </div>
          <div className="col-md-3">
            <label className="form-label fw-medium">{t("finance.transfers.list.club")}</label>
            <select className="form-select" value={filters.clubId || ""} onChange={(e) => onFilterChange("clubId", e.target.value)}>
              <option value="">{t("finance.transfers.list.allClubs")}</option>
              {clubs.map((club) => <option key={club.id} value={club.id}>{club.nameEn}</option>)}
            </select>
          </div>
          <div className="col-md-3">
            <label className="form-label fw-medium">{t("finance.transfers.list.type")}</label>
            <select className="form-select" value={filters.type || ""} onChange={(e) => onFilterChange("type", e.target.value)}>
              <option value="">{t("finance.transfers.list.allTypes")}</option>
              <option value="APPLYING_EVENT">{t("finance.transfers.list.eventType")}</option>
              <option value="JOINING_CLUB">{t("finance.transfers.list.clubType")}</option>
            </select>
          </div>
          <div className="col-md-3">
            <label className="form-label fw-medium">{t("finance.transfers.list.transferType")}</label>
            <select className="form-select" value={filters.transferType || ""} onChange={(e) => onFilterChange("transferType", e.target.value)}>
              <option value="">{t("finance.transfers.list.allTransferTypes")}</option>
              <option value="PAYMENT">{t("finance.transfers.list.payment")}</option>
              <option value="REFUND">{t("finance.transfers.list.refund")}</option>
            </select>
          </div>
          <div className="col-md-3">
            <label className="form-label fw-medium">{t("finance.transfers.list.status")}</label>
            <select className="form-select" value={filters.status || ""} onChange={(e) => onFilterChange("status", e.target.value)}>
              <option value="">{t("finance.transfers.list.allStatuses")}</option>
              <option value="COMPLETED">{t("finance.transfers.list.completed")}</option>
              <option value="PENDING">{t("finance.transfers.list.pending")}</option>
              <option value="FAILED">{t("finance.transfers.list.failed")}</option>
              <option value="REFUNDED">{t("finance.transfers.list.refunded")}</option>
            </select>
          </div>
          {hasActiveFilters && (
            <div className="col-auto d-flex align-items-end">
              <AppButton variant="outline" className="btn-sm" onClick={onClearFilters}>
                <i className="fas fa-times me-1"></i>{tCommon("buttons.clearFilters")}
              </AppButton>
            </div>
          )}
        </div>
      </ListToolbar>

      <div className="card border-0 shadow-sm">
        <div className="card-body p-0">
          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status"><span className="visually-hidden">{tCommon("actions.loading")}</span></div>
              <p className="text-muted mt-2">{t("finance.transfers.list.loading")}</p>
            </div>
          ) : transfers.length === 0 ? (
            <div className="text-center py-5">
              <i className="fas fa-exchange-alt text-muted mb-3" style={{ fontSize: "3rem" }}></i>
              <h5 className="text-muted">{t("finance.transfers.list.noFound")}</h5>
              <p className="text-muted">{hasActiveFilters ? t("finance.transfers.list.adjustFilters") : t("finance.transfers.list.noneCreated")}</p>
            </div>
          ) : (
            <AppTable responsive>
              <thead>
                <tr>
                  <th className="ps-4">{t("finance.transfers.list.columns.from")}</th>
                  <th>{t("finance.transfers.list.columns.toClub")}</th>
                  <th className="text-center">{t("finance.transfers.list.columns.amount")}</th>
                  <th>{t("finance.transfers.list.columns.date")}</th>
                  <th className="text-center">{t("finance.transfers.list.columns.type")}</th>
                  <th className="text-center">{t("finance.transfers.list.columns.transferType")}</th>
                  <th className="text-center pe-4">{t("finance.transfers.list.columns.status")}</th>
                </tr>
              </thead>
              <tbody>
                {transfers.map((transfer) => (
                  <tr key={transfer.id} className="border-bottom">
                    <td className="ps-4">
                      <div className="text-dark"><i className="fas fa-user me-1 text-primary"></i>{transfer.fromUser}</div>
                    </td>
                    <td>
                      <div className="fw-bold text-dark">{transfer.toClub}</div>
                      <small className="text-muted">{transfer.clubNameEn}</small>
                    </td>
                    <td className="text-center">
                      {transfer.amount === 0 ? (
                        <span className="badge bg-secondary bg-opacity-10 text-secondary">{t("finance.transfers.list.free")}</span>
                      ) : (
                        <span className="fw-bold text-dark">{transfer.amount} {transfer.currency}</span>
                      )}
                    </td>
                    <td>
                      <span className="text-dark"><i className="fas fa-calendar me-1 text-info"></i>{formatNumericDate(transfer.date, i18n.language)}</span>
                    </td>
                    <td className="text-center">
                      <span className={`badge ${transfer.type === "APPLYING_EVENT" ? "bg-info" : "bg-primary"} bg-opacity-10 text-${transfer.type === "APPLYING_EVENT" ? "info" : "primary"}`}>
                        {transfer.type === "APPLYING_EVENT" ? t("finance.transfers.list.eventType") : t("finance.transfers.list.clubType")}
                      </span>
                    </td>
                    <td className="text-center">
                      <span className={`badge ${transfer.transferType === "PAYMENT" ? "bg-primary" : "bg-secondary"} bg-opacity-10 text-${transfer.transferType === "PAYMENT" ? "primary" : "secondary"}`}>
                        {transfer.transferType === "PAYMENT" ? t("finance.transfers.list.payment") : t("finance.transfers.list.refund")}
                      </span>
                    </td>
                    <td className="text-center pe-4">{getStatusBadge(transfer.status)}</td>
                  </tr>
                ))}
              </tbody>
            </AppTable>
          )}
        </div>
      </div>
    </div>
  );
};

export default UMTransferList;
